import logo from "../../assets/top-nav-logo.svg";
import { currencyFormatter, formatDate } from "../../utility-functions";

export function generateProformaInvoicePdf(data) {
  if (!data) return;

  const {
    date,
    payment_terms,
    disclaimer,
    company_details,
    bank_details,
    customer,
    subscription,
    proration_days,
    line_items,
    subtotal,
    tax_percentage,
    tax_amount,
    total,
    tds_tax_name,
    tds_tax_rate,
    tds_amount,
  } = data;

  const currency = subscription?.currency || "INR";
  const sym = currency === "INR" ? "₹" : "$";

  const addr = customer?.billing_address || {};
  const addrParts = [
    addr.street,
    addr.city,
    addr.state,
    addr.zip || addr.zipcode,
    addr.country,
  ].filter(Boolean);

  const lineItemsHtml = line_items
    ?.map((item, idx) => {
      const prorLabel =
        item.type === "one_time_fee"
          ? "One-time"
          : `${proration_days} days remaining`;
      return `
      <tr>
        <td style="text-align:center">${idx + 1}</td>
        <td>${item.description}</td>
        <td style="text-align:center">${item.quantity}</td>
        <td style="text-align:right">${currencyFormatter(sym, item.unit_price)}</td>
        <td style="text-align:center">${prorLabel}</td>
        <td style="text-align:right">${currencyFormatter(sym, item.amount)}</td>
      </tr>`;
    })
    .join("");

  let bankHtml = "";
  if (bank_details) {
    bankHtml += `<strong>Payment Details</strong><br/>`;
    bankHtml += `Bank Name: ${bank_details.bank_name}<br/>`;
    if (bank_details.account_number)
      bankHtml += `Account Number: ${bank_details.account_number}<br/>`;
    if (bank_details.routing_number)
      bankHtml += `Routing Number: ${bank_details.routing_number}<br/>`;
    if (bank_details.ifsc_code)
      bankHtml += `IFSC Code: ${bank_details.ifsc_code}<br/>`;
    if (bank_details.swift_code)
      bankHtml += `SWIFT Code: ${bank_details.swift_code}<br/>`;
  }

  const html = `<!DOCTYPE html>
<html>
<head>
  <title>Proforma Invoice - FreJun</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, sans-serif;
      padding: 48px 56px;
      color: #1a1a2e;
      font-size: 12px;
      line-height: 1.5;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 32px;
    }
    .logo svg { width: 100px; height: auto; }
    .title {
      font-size: 22px;
      font-weight: 700;
      letter-spacing: 2px;
      text-align: right;
    }
    .company-info {
      margin-bottom: 24px;
      font-size: 12px;
      line-height: 1.6;
    }
    .company-info strong { font-size: 13px; }
    .meta-table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 24px;
    }
    .meta-table td {
      border: 1px solid #d1d5db;
      padding: 6px 12px;
      font-size: 12px;
    }
    .meta-table td:first-child {
      background: #f9fafb;
      font-weight: 600;
      width: 200px;
      color: #374151;
    }
    .section-title {
      font-size: 13px;
      font-weight: 700;
      margin-bottom: 6px;
      margin-top: 20px;
    }
    .bill-to {
      margin-bottom: 24px;
      font-size: 12px;
      line-height: 1.6;
    }
    .items-table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 20px;
    }
    .items-table th {
      background: #f3f4f6;
      border: 1px solid #d1d5db;
      padding: 8px 10px;
      font-size: 11px;
      font-weight: 600;
      text-transform: uppercase;
      color: #374151;
    }
    .items-table td {
      border: 1px solid #d1d5db;
      padding: 7px 10px;
      font-size: 12px;
    }
    .totals-table {
      width: 320px;
      margin-left: auto;
      border-collapse: collapse;
      margin-bottom: 28px;
    }
    .totals-table td {
      border: 1px solid #d1d5db;
      padding: 6px 12px;
      font-size: 12px;
    }
    .totals-table td:first-child {
      background: #f9fafb;
      font-weight: 600;
      color: #374151;
    }
    .totals-table tr:last-child td {
      font-weight: 700;
      font-size: 13px;
    }
    .bank-details {
      margin-bottom: 24px;
      font-size: 12px;
      line-height: 1.7;
    }
    .disclaimer {
      font-size: 11px;
      line-height: 1.6;
      color: #6b7280;
      margin-top: 16px;
    }
    .disclaimer strong { color: #374151; }
    @media print {
      body { padding: 32px 40px; }
    }
  </style>
</head>
<body>
  <div class="header">
    <div class="logo"><img src="${logo}" alt="logo" /></div>
    <div class="title">PROFORMA INVOICE</div>
  </div>

  <div class="company-info">
    <strong>${company_details?.name || ""}</strong><br/>
    ${company_details?.address || ""}<br/>
    ${company_details?.email || ""}
  </div>

  <table class="meta-table">
    <tr><td>Proforma Date</td><td>${formatDate(date)}</td></tr>
    <tr><td>Billing Cycle Start</td><td>${formatDate(subscription?.start_date) || ""}</td></tr>
    <tr><td>Billing Cycle End</td><td>${formatDate(subscription?.end_date) || ""}</td></tr>
    <tr><td>Currency</td><td>${currency}</td></tr>
    <tr><td>Payment Terms</td><td>${payment_terms || "Due on Confirmation"}</td></tr>
  </table>

  <div class="section-title">Bill To</div>
  <div class="bill-to">
    ${customer?.company_name || ""}<br/>
    ${addrParts.join("<br/>")}
    ${customer?.gstin ? `<br/>GSTIN: ${customer.gstin}` : ""}
  </div>

  <table class="items-table">
    <thead>
      <tr>
        <th style="width:30px;text-align:center">#</th>
        <th style="text-align:left">Item Description</th>
        <th style="width:50px;text-align:center">Qty</th>
        <th style="width:90px;text-align:right">Unit Price<br/><span style="font-weight:400;text-transform:none">(Per User)</span></th>
        <th style="width:120px;text-align:center">Proration</th>
        <th style="width:90px;text-align:right">Amount</th>
      </tr>
    </thead>
    <tbody>
      ${lineItemsHtml}
    </tbody>
  </table>

  <table class="totals-table">
    <tr><td>Subtotal</td><td style="text-align:right">${currencyFormatter(sym, subtotal)}</td></tr>
    ${
      Number(tds_amount) > 0
        ? `<tr><td>${tds_tax_name || "TDS"} @ ${tds_tax_rate}%</td><td style="text-align:right;color:#dc2626">- ${currencyFormatter(sym, tds_amount)}</td></tr>`
        : ""
    }
    <tr><td>Tax${tax_percentage ? ` (${tax_percentage}% ${currency === "INR" ? "GST" : ""})` : ""}</td><td style="text-align:right">${currencyFormatter(sym, tax_amount)}</td></tr>
    <tr><td>Total</td><td style="text-align:right">${currencyFormatter(sym, total)}</td></tr>
  </table>

  ${bankHtml ? `<div class="bank-details">${bankHtml}</div>` : ""}

  <div class="disclaimer">
    <strong>Disclaimer:</strong> ${disclaimer || ""}
  </div>
</body>
</html>`;

  const printWindow = window.open("", "_blank");
  if (!printWindow) {
    alert("Please allow popups to download the proforma invoice.");
    return;
  }
  printWindow.document.write(html);
  printWindow.document.close();
  printWindow.focus();
  setTimeout(() => {
    printWindow.print();
  }, 400);
}
